import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { AxiosError } from 'axios';
import client from '../api/client';
import { formatPolicyStartDisplay } from '../components/PolicyStartDateField';
import { getPetAgeLabel } from '../data/petAgeCategories';
import { getFriendlyApiError } from '../utils/apiErrors';

function insuranceRequestsFetchErrorMessage(err: unknown): string {
  const ax = err as AxiosError<{ error?: string }>;
  const apiErr = ax.response?.data?.error;
  if (typeof apiErr === 'string' && apiErr.trim()) return apiErr;

  if (ax.code === 'ECONNABORTED') {
    return 'Loading took longer than expected and timed out. Try again in a moment.';
  }

  const status = ax.response?.status;
  if (status === 504 || status === 503) {
    return 'The server took too long to respond. Try again shortly.';
  }
  if (status === 500) {
    return 'Something went wrong loading your requests. Try again shortly.';
  }

  return getFriendlyApiError(err, 'Failed to load requests');
}

interface RentersInsuranceRequest {
  id: number;
  insuranceType: string;
  zipCode: string;
  streetAddress: string;
  addressLine2: string | null;
  city: string;
  state: string;
  country: string;
  safetyFeatures: string[];
  buildingType: string;
  hasExistingRentersInsurance: boolean;
  hasRoommates: boolean;
  personalLiabilityLimit?: number | null;
  roommateCount?: number | null;
  policyStartDate?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface CarInsuranceVehicle {
  id: number;
  vehicleYear: number;
  vehicleMake: string;
  vehicleModel: string;
  vehicleVin: string;
  vinPendingLater?: boolean;
  coverageType: string;
}

interface CarInsuranceAdditionalDriver {
  id: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  txDl: string;
  email?: string;
}

interface PetInsuranceQuoteOptions {
  annualLimit: string;
  reimbursementPercent: number;
  deductibleAmount: number;
}

interface PetInsuranceRequest {
  id: number;
  insuranceType: string;
  zipCode: string;
  contactEmail: string | null;
  policyStartDate?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  pet: {
    id: number;
    name: string;
    species: string;
    breed: string;
    ageCategory: string;
    weightLbs: number | null;
    spayedNeutered: boolean;
  };
  quoteOptions: PetInsuranceQuoteOptions | null;
}

interface CarInsuranceRequest {
  id: number;
  applicantFullName: string | null;
  applicantDateOfBirth: string | null;
  applicantTxDl: string | null;
  homeStreetAddress: string | null;
  homeAddressLine2: string | null;
  homeCity: string | null;
  homeState: string | null;
  homeZip: string | null;
  contactEmail: string | null;
  additionalDrivers?: CarInsuranceAdditionalDriver[];
  hasActiveAutoPolicy: boolean | null;
  declarationDocumentPath: string | null;
  declarationPendingLater?: boolean;
  vehicles: CarInsuranceVehicle[];
  policyStartDate?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface HomeInsuranceRequest {
  id: number;
  zipCode: string;
  streetAddress: string;
  addressLine2: string | null;
  city: string;
  state: string;
  isNewHomePurchase: boolean;
  priorStreetAddress: string | null;
  priorAddressLine2: string | null;
  priorCity: string | null;
  priorState: string | null;
  priorZip: string | null;
  applicantDateOfBirth: string | null;
  spouseFullName: string | null;
  spouseDateOfBirth: string | null;
  yearRoofReplaced: number | null;
  yearWaterHeaterReplaced: number | null;
  waterHeaterLocation: string | null;
  waterHeaterCount: number | null;
  yearPlumbingUpdated: number | null;
  yearAcHeatingReplaced: number | null;
  acUnitCount: number | null;
  yearElectricalPanelReplaced: number | null;
  hasCentralAlarm: boolean | null;
  hasTrampoline: boolean | null;
  hasPets: boolean | null;
  hasSolarPanels: boolean | null;
  hasPermanentGenerator: boolean | null;
  declarationDocumentPath: string | null;
  declarationPendingLater?: boolean;
  propertyDetailsPendingLater?: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
}

function declarationFileHref(path: string | null | undefined): string | null {
  if (!path) return null;
  const base = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
  const origin = base.replace(/\/?api\/?$/, '');
  return `${origin}${path}`;
}

function formatCoverageType(t: string): string {
  if (t === 'liability_only') return 'Liability only';
  if (t === 'full_coverage') return 'Full coverage';
  return t;
}

function formatPetSpecies(species: string): string {
  if (species === 'dog') return 'Dog';
  if (species === 'cat') return 'Cat';
  return species;
}

function formatPetAnnualLimit(value: string | null | undefined): string {
  if (!value || value === 'decide_later') return 'Decide later';
  if (value === 'unlimited') return 'Unlimited';
  const n = Number(value);
  if (!Number.isNaN(n)) return `$${n.toLocaleString()}`;
  return value;
}

function formatPetReimbursement(value: number | null | undefined): string {
  if (value == null || value === 0) return 'Decide later';
  return `${value}%`;
}

function formatPetDeductible(value: number | null | undefined): string {
  if (value == null) return 'Decide later';
  if (value === 0) return 'Decide later';
  return `$${value.toLocaleString()}`;
}

type CombinedRow =
  | { kind: 'renters'; request: RentersInsuranceRequest; sortKey: number }
  | { kind: 'car'; request: CarInsuranceRequest; sortKey: number }
  | { kind: 'pet'; request: PetInsuranceRequest; sortKey: number }
  | { kind: 'home'; request: HomeInsuranceRequest; sortKey: number };

function formatYesNo(value: boolean | null | undefined): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '—';
}

function InsuranceRequestsPage() {
  const [rows, setRows] = useState<CombinedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replayWarning, setReplayWarning] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [confirmDeleteKey, setConfirmDeleteKey] = useState<string | null>(null);

  useEffect(() => {
    const warning = sessionStorage.getItem('insuranceReplayWarning');
    if (warning) {
      setReplayWarning(warning);
      sessionStorage.removeItem('insuranceReplayWarning');
    }
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const [rentersRes, carRes, petRes, homeRes] = await Promise.all([
        client.get(`/insurance/requests${q}`),
        client.get(`/insurance/car-requests${q}`),
        client.get(`/insurance/pet-requests${q}`),
        client.get(`/insurance/home-requests${q}`),
      ]);

      const rentersList: RentersInsuranceRequest[] = rentersRes.data.requests || [];
      const carList: CarInsuranceRequest[] = carRes.data.requests || [];
      const petList: PetInsuranceRequest[] = petRes.data.requests || [];
      const homeList: HomeInsuranceRequest[] = homeRes.data.requests || [];

      const combined: CombinedRow[] = [
        ...rentersList.map((request) => ({
          kind: 'renters' as const,
          request,
          sortKey: new Date(request.createdAt).getTime(),
        })),
        ...carList.map((request) => ({
          kind: 'car' as const,
          request,
          sortKey: new Date(request.createdAt).getTime(),
        })),
        ...petList.map((request) => ({
          kind: 'pet' as const,
          request,
          sortKey: new Date(request.createdAt).getTime(),
        })),
        ...homeList.map((request) => ({
          kind: 'home' as const,
          request,
          sortKey: new Date(request.createdAt).getTime(),
        })),
      ].sort((a, b) => b.sortKey - a.sortKey);

      setRows(combined);
    } catch (err: unknown) {
      setError(insuranceRequestsFetchErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRenters = async (id: number) => {
    await client.delete(`/insurance/requests/${id}`);
    setRows((prev) => prev.filter((r) => !(r.kind === 'renters' && r.request.id === id)));
    setConfirmDeleteKey(null);
    if (expandedKey === `renters-${id}`) setExpandedKey(null);
  };

  const handleDeleteCar = async (id: number) => {
    await client.delete(`/insurance/car-requests/${id}`);
    setRows((prev) => prev.filter((r) => !(r.kind === 'car' && r.request.id === id)));
    setConfirmDeleteKey(null);
    if (expandedKey === `car-${id}`) setExpandedKey(null);
  };

  const handleDeletePet = async (id: number) => {
    await client.delete(`/insurance/pet-requests/${id}`);
    setRows((prev) => prev.filter((r) => !(r.kind === 'pet' && r.request.id === id)));
    setConfirmDeleteKey(null);
    if (expandedKey === `pet-${id}`) setExpandedKey(null);
  };

  const handleDeleteHome = async (id: number) => {
    await client.delete(`/insurance/home-requests/${id}`);
    setRows((prev) => prev.filter((r) => !(r.kind === 'home' && r.request.id === id)));
    setConfirmDeleteKey(null);
    if (expandedKey === `home-${id}`) setExpandedKey(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  /** API may return DATE columns as `YYYY-MM-DDT00:00:00.000Z`; show date only. */
  const formatDob = (value: string | null | undefined) => {
    if (!value) return '—';
    const isoDate = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDate) return isoDate[1];
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
  };

  const formatCurrency = (value?: number | null) => {
    if (!value || value <= 0) return '-';
    return `$${value.toLocaleString()}`;
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, { bg: string; text: string }> = {
      pending: { bg: '#FEF3C7', text: '#92400E' },
      approved: { bg: '#D1FAE5', text: '#065F46' },
      rejected: { bg: '#FEE2E2', text: '#7F1D1D' },
    };
    const color = colors[status] || colors.pending;
    return (
      <span
        style={{
          backgroundColor: color.bg,
          color: color.text,
          padding: '0.25rem 0.75rem',
          borderRadius: '0.25rem',
          fontSize: '0.85rem',
          fontWeight: '600',
        }}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="container">
      <div className="flex-between mb-3 insurance-requests-page-header">
        <h2>My Insurance Requests</h2>
        <Link to="/qualify" className="btn btn-primary">
          New Request
        </Link>
      </div>

      <div className="mb-3" style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <label htmlFor="filter" style={{ fontWeight: '600', marginBottom: 0 }}>
          Filter by Status:
        </label>
        <select
          id="filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '0.5rem 1rem', borderRadius: '0.25rem' }}
        >
          <option value="">All Requests</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {replayWarning && (
        <div className="alert alert-error" role="status">
          {replayWarning}
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
          Loading your insurance requests...
        </div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <p>No insurance requests yet.</p>
          <Link to="/qualify" className="btn btn-primary mt-2">
            Submit Your First Request
          </Link>
        </div>
      ) : (
        <div className="grid grid-1">
          {rows.map((row) => {
            if (row.kind === 'home') {
              const request = row.request;
              const key = `home-${request.id}`;
              const expanded = expandedKey === key;
              const declHref = declarationFileHref(request.declarationDocumentPath);
              return (
                <div key={key} className="card">
                  <div className="flex-between mb-2">
                    <h3 style={{ margin: 0, color: 'var(--color-green)' }}>Home Insurance</h3>
                    {getStatusBadge(request.status)}
                  </div>

                  <div className="insurance-request-summary-grid">
                    <div>
                      <span style={{ color: '#999', fontSize: '0.9rem' }}>Address</span>
                      <p className="insurance-request-breakable" style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>
                        {request.streetAddress}
                        {request.addressLine2 ? `, ${request.addressLine2}` : ''}, {request.city}, {request.state}{' '}
                        {request.zipCode}
                      </p>
                    </div>
                    <div>
                      <span style={{ color: '#999', fontSize: '0.9rem' }}>Roof replaced</span>
                      <p style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>
                        {request.propertyDetailsPendingLater ? 'Agent follow-up' : request.yearRoofReplaced ?? '—'}
                      </p>
                    </div>
                    <div>
                      <span style={{ color: '#999', fontSize: '0.9rem' }}>Central alarm</span>
                      <p style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>
                        {request.propertyDetailsPendingLater ? 'Agent follow-up' : formatYesNo(request.hasCentralAlarm)}
                      </p>
                    </div>
                    <div>
                      <span style={{ color: '#999', fontSize: '0.9rem' }}>Pets</span>
                      <p style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>
                        {request.propertyDetailsPendingLater ? 'Agent follow-up' : formatYesNo(request.hasPets)}
                      </p>
                    </div>
                  </div>

                  {expanded && (
                    <div
                      style={{
                        background: 'var(--color-light-grey)',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '1rem',
                      }}
                    >
                      {request.propertyDetailsPendingLater && (
                        <p style={{ margin: '0 0 1rem', color: '#374151' }}>
                          Property details were deferred — an agent will reach out to collect home systems and feature
                          information.
                        </p>
                      )}
                      <div className="insurance-request-detail-grid">
                        <div>
                          <span style={{ color: '#6b7280' }}>Your date of birth</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>{formatDob(request.applicantDateOfBirth)}</p>
                        </div>
                        {request.spouseFullName && (
                          <>
                            <div>
                              <span style={{ color: '#6b7280' }}>Spouse</span>
                              <p style={{ margin: 0, fontWeight: 600 }}>{request.spouseFullName}</p>
                            </div>
                            <div>
                              <span style={{ color: '#6b7280' }}>Spouse DOB</span>
                              <p style={{ margin: 0, fontWeight: 600 }}>{formatDob(request.spouseDateOfBirth)}</p>
                            </div>
                          </>
                        )}
                        {request.isNewHomePurchase && request.priorStreetAddress && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <span style={{ color: '#6b7280' }}>Prior address</span>
                            <p className="insurance-request-breakable" style={{ margin: 0, fontWeight: 600 }}>
                              {request.priorStreetAddress}
                              {request.priorAddressLine2 ? `, ${request.priorAddressLine2}` : ''}, {request.priorCity},{' '}
                              {request.priorState} {request.priorZip}
                            </p>
                          </div>
                        )}
                        <div>
                          <span style={{ color: '#6b7280' }}>Declarations page</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>
                            {request.declarationPendingLater
                              ? 'To be provided later'
                              : declHref
                                ? (
                                    <a href={declHref} target="_blank" rel="noopener noreferrer">
                                      View uploaded file
                                    </a>
                                  )
                                : '—'}
                          </p>
                        </div>
                        {!request.propertyDetailsPendingLater && (
                          <>
                        <div>
                          <span style={{ color: '#6b7280' }}>Water heater replaced</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>{request.yearWaterHeaterReplaced ?? '—'}</p>
                        </div>
                        <div>
                          <span style={{ color: '#6b7280' }}>Water heater location</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>{request.waterHeaterLocation ?? '—'}</p>
                        </div>
                        <div>
                          <span style={{ color: '#6b7280' }}>Water heaters</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>{request.waterHeaterCount ?? '—'}</p>
                        </div>
                        <div>
                          <span style={{ color: '#6b7280' }}>Plumbing updated</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>{request.yearPlumbingUpdated ?? '—'}</p>
                        </div>
                        <div>
                          <span style={{ color: '#6b7280' }}>AC/heating replaced</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>{request.yearAcHeatingReplaced ?? '—'}</p>
                        </div>
                        <div>
                          <span style={{ color: '#6b7280' }}>AC units</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>{request.acUnitCount ?? '—'}</p>
                        </div>
                        <div>
                          <span style={{ color: '#6b7280' }}>Electrical panel replaced</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>{request.yearElectricalPanelReplaced ?? '—'}</p>
                        </div>
                        <div>
                          <span style={{ color: '#6b7280' }}>Trampoline</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>{formatYesNo(request.hasTrampoline)}</p>
                        </div>
                        <div>
                          <span style={{ color: '#6b7280' }}>Solar panels</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>{formatYesNo(request.hasSolarPanels)}</p>
                        </div>
                        <div>
                          <span style={{ color: '#6b7280' }}>Permanent generator</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>{formatYesNo(request.hasPermanentGenerator)}</p>
                        </div>
                          </>
                        )}
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
                        {confirmDeleteKey === key ? (
                          <button className="btn btn-danger" onClick={() => handleDeleteHome(request.id)}>
                            Confirm Delete?
                          </button>
                        ) : (
                          <button className="btn btn-outline" onClick={() => setConfirmDeleteKey(key)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '0.75rem',
                      fontSize: '0.9rem',
                      color: '#999',
                      borderTop: '1px solid var(--color-grey)',
                      paddingTop: '1rem',
                    }}
                  >
                    <span style={{ minWidth: 0 }}>Submitted: {formatDate(request.createdAt)}</span>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      style={{ flexShrink: 0 }}
                      onClick={() => setExpandedKey(expanded ? null : key)}
                    >
                      {expanded ? 'Hide Details' : 'View Details'}
                    </button>
                  </div>
                </div>
              );
            }

            if (row.kind === 'pet') {
              const request = row.request;
              const key = `pet-${request.id}`;
              const expanded = expandedKey === key;
              const pet = request.pet;
              const quote = request.quoteOptions;
              return (
                <div key={key} className="card">
                  <div className="flex-between mb-2">
                    <h3 style={{ margin: 0, color: 'var(--color-green)' }}>Pet Insurance</h3>
                    {getStatusBadge(request.status)}
                  </div>

                  <div className="insurance-request-summary-grid">
                    <div>
                      <span style={{ color: '#999', fontSize: '0.9rem' }}>Pet</span>
                      <p style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>
                        {pet.name} ({formatPetSpecies(pet.species)})
                      </p>
                    </div>
                    <div>
                      <span style={{ color: '#999', fontSize: '0.9rem' }}>Zip code</span>
                      <p style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>{request.zipCode}</p>
                    </div>
                    <div>
                      <span style={{ color: '#999', fontSize: '0.9rem' }}>Breed</span>
                      <p className="insurance-request-breakable" style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>
                        {pet.breed}
                      </p>
                    </div>
                    <div>
                      <span style={{ color: '#999', fontSize: '0.9rem' }}>Age</span>
                      <p style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>{getPetAgeLabel(pet.ageCategory)}</p>
                    </div>
                  </div>

                  {expanded && (
                    <div
                      style={{
                        background: 'var(--color-light-grey)',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '1rem',
                      }}
                    >
                      <div className="insurance-request-detail-grid">
                        <div>
                          <span style={{ color: '#6b7280' }}>Weight</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>
                            {pet.weightLbs != null ? `${pet.weightLbs} lbs` : '—'}
                          </p>
                        </div>
                        <div>
                          <span style={{ color: '#6b7280' }}>Spayed / neutered</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>{pet.spayedNeutered ? 'Yes' : 'No'}</p>
                        </div>
                        <div>
                          <span style={{ color: '#6b7280' }}>Policy start</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>
                            {formatPolicyStartDisplay(request.policyStartDate)}
                          </p>
                        </div>
                        {request.contactEmail && (
                          <div>
                            <span style={{ color: '#6b7280' }}>Contact email</span>
                            <p className="insurance-request-breakable" style={{ margin: 0, fontWeight: 600 }}>
                              {request.contactEmail}
                            </p>
                          </div>
                        )}
                        {quote && (
                          <>
                            <div>
                              <span style={{ color: '#6b7280' }}>Annual limit</span>
                              <p style={{ margin: 0, fontWeight: 600 }}>
                                {formatPetAnnualLimit(quote.annualLimit)}
                              </p>
                            </div>
                            <div>
                              <span style={{ color: '#6b7280' }}>Reimbursement</span>
                              <p style={{ margin: 0, fontWeight: 600 }}>
                                {formatPetReimbursement(quote.reimbursementPercent)}
                              </p>
                            </div>
                            <div>
                              <span style={{ color: '#6b7280' }}>Deductible</span>
                              <p style={{ margin: 0, fontWeight: 600 }}>
                                {formatPetDeductible(quote.deductibleAmount)}
                              </p>
                            </div>
                          </>
                        )}
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
                        {confirmDeleteKey === key ? (
                          <button className="btn btn-danger" onClick={() => handleDeletePet(request.id)}>
                            Confirm Delete?
                          </button>
                        ) : (
                          <button className="btn btn-outline" onClick={() => setConfirmDeleteKey(key)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '0.75rem',
                      fontSize: '0.9rem',
                      color: '#999',
                      borderTop: '1px solid var(--color-grey)',
                      paddingTop: '1rem',
                    }}
                  >
                    <span style={{ minWidth: 0 }}>Submitted: {formatDate(request.createdAt)}</span>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      style={{ flexShrink: 0 }}
                      onClick={() => setExpandedKey(expanded ? null : key)}
                    >
                      {expanded ? 'Hide Details' : 'View Details'}
                    </button>
                  </div>
                </div>
              );
            }

            if (row.kind === 'car') {
              const request = row.request;
              const key = `car-${request.id}`;
              const expanded = expandedKey === key;
              const primaryName = request.applicantFullName || '—';
              const vehCount = request.vehicles?.length ?? 0;
              const declHref = declarationFileHref(request.declarationDocumentPath || undefined);
              return (
                <div key={key} className="card">
                  <div className="flex-between mb-2">
                    <h3 style={{ margin: 0, color: 'var(--color-green)' }}>Car Insurance</h3>
                    {getStatusBadge(request.status)}
                  </div>

                  <div className="insurance-request-summary-grid">
                    <div>
                      <span style={{ color: '#999', fontSize: '0.9rem' }}>Applicant</span>
                      <p className="insurance-request-breakable" style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>
                        {primaryName}
                      </p>
                    </div>
                    <div>
                      <span style={{ color: '#999', fontSize: '0.9rem' }}>Contact email</span>
                      <p className="insurance-request-breakable" style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>
                        {request.contactEmail || '—'}
                      </p>
                    </div>
                    <div>
                      <span style={{ color: '#999', fontSize: '0.9rem' }}>Active auto policy</span>
                      <p style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>
                        {request.hasActiveAutoPolicy ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div>
                      <span style={{ color: '#999', fontSize: '0.9rem' }}>Vehicles on file</span>
                      <p style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>{vehCount}</p>
                    </div>
                  </div>

                  {expanded && (
                    <div
                      style={{
                        background: 'var(--color-light-grey)',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '1rem',
                      }}
                    >
                      <div className="insurance-request-detail-grid">
                        <div>
                          <span style={{ color: '#6b7280' }}>DOB</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>{formatDob(request.applicantDateOfBirth)}</p>
                        </div>
                        <div>
                          <span style={{ color: '#6b7280' }}>TX DL</span>
                          <p className="insurance-request-breakable" style={{ margin: 0, fontWeight: 600 }}>
                            {request.applicantTxDl || 'Add later'}
                          </p>
                        </div>
                        <div>
                          <span style={{ color: '#6b7280' }}>Policy start</span>
                          <p style={{ margin: 0, fontWeight: 600 }}>
                            {formatPolicyStartDisplay(request.policyStartDate)}
                          </p>
                        </div>
                        <div className="insurance-request-grid-full">
                          <span style={{ color: '#6b7280' }}>Home address</span>
                          <p className="insurance-request-breakable" style={{ margin: 0, fontWeight: 600 }}>
                            {[request.homeStreetAddress, request.homeAddressLine2, request.homeCity, request.homeState, request.homeZip]
                              .filter(Boolean)
                              .join(', ') || '—'}
                          </p>
                        </div>
                        {(request.additionalDrivers?.length || 0) > 0 && (
                          <div className="insurance-request-grid-full">
                            <span style={{ color: '#6b7280' }}>Additional drivers</span>
                            <div className="insurance-request-addl-drivers" style={{ marginTop: '0.5rem' }}>
                              {request.additionalDrivers!.map((d) => (
                                <div key={d.id} className="insurance-request-addl-driver-card">
                                  <div className="insurance-request-addl-driver-field">
                                    <span className="insurance-request-addl-driver-label">Name</span>
                                    <span className="insurance-request-addl-driver-value">
                                      {`${d.firstName} ${d.lastName}`.trim() || '—'}
                                    </span>
                                  </div>
                                  <div className="insurance-request-addl-driver-field">
                                    <span className="insurance-request-addl-driver-label">Date of birth</span>
                                    <span className="insurance-request-addl-driver-value">{formatDob(d.dateOfBirth)}</span>
                                  </div>
                                  <div className="insurance-request-addl-driver-field">
                                    <span className="insurance-request-addl-driver-label">TX DL</span>
                                    <span className="insurance-request-addl-driver-value">{d.txDl || '—'}</span>
                                  </div>
                                  <div className="insurance-request-addl-driver-field">
                                    <span className="insurance-request-addl-driver-label">Email</span>
                                    <span className="insurance-request-addl-driver-value">{d.email || '—'}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {request.declarationPendingLater && !declHref && (
                          <div className="insurance-request-grid-full">
                            <span style={{ color: '#6b7280' }}>Declarations page</span>
                            <p style={{ margin: '0.25rem 0 0', fontWeight: 600 }}>To be provided later</p>
                          </div>
                        )}
                        {declHref && (
                          <div className="insurance-request-grid-full">
                            <span style={{ color: '#6b7280' }}>Declarations page</span>
                            <p style={{ margin: '0.25rem 0 0' }}>
                              <a href={declHref} target="_blank" rel="noopener noreferrer">
                                Open uploaded file
                              </a>
                            </p>
                          </div>
                        )}
                      </div>

                      {vehCount > 0 && (
                        <div style={{ marginTop: '1rem' }}>
                          <span style={{ color: '#6b7280', fontWeight: 600 }}>Vehicles</span>
                          <div className="insurance-request-vehicles">
                            <div className="insurance-request-vehicles-head" aria-hidden="true">
                              <span>Year</span>
                              <span>Make</span>
                              <span>Model</span>
                              <span>VIN</span>
                              <span>Coverage</span>
                            </div>
                            {request.vehicles.map((v) => (
                              <div key={v.id} className="insurance-request-vehicle-card">
                                <div className="insurance-request-vehicle-field">
                                  <span className="insurance-request-vehicle-label">Year</span>
                                  <span className="insurance-request-vehicle-value">{v.vehicleYear}</span>
                                </div>
                                <div className="insurance-request-vehicle-field">
                                  <span className="insurance-request-vehicle-label">Make</span>
                                  <span className="insurance-request-vehicle-value">{v.vehicleMake}</span>
                                </div>
                                <div className="insurance-request-vehicle-field">
                                  <span className="insurance-request-vehicle-label">Model</span>
                                  <span className="insurance-request-vehicle-value">{v.vehicleModel}</span>
                                </div>
                                <div className="insurance-request-vehicle-field">
                                  <span className="insurance-request-vehicle-label">VIN</span>
                                  <span
                                    className={`insurance-request-vehicle-value${
                                      !v.vinPendingLater ? ' insurance-request-vehicle-value--vin' : ''
                                    }`}
                                  >
                                    {v.vinPendingLater ? 'To be provided later' : v.vehicleVin}
                                  </span>
                                </div>
                                <div className="insurance-request-vehicle-field">
                                  <span className="insurance-request-vehicle-label">Coverage</span>
                                  <span className="insurance-request-vehicle-value">{formatCoverageType(v.coverageType)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
                        {confirmDeleteKey === key ? (
                          <button className="btn btn-danger" onClick={() => handleDeleteCar(request.id)}>
                            Confirm Delete?
                          </button>
                        ) : (
                          <button className="btn btn-outline" onClick={() => setConfirmDeleteKey(key)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '0.75rem',
                      fontSize: '0.9rem',
                      color: '#999',
                      borderTop: '1px solid var(--color-grey)',
                      paddingTop: '1rem',
                    }}
                  >
                    <span style={{ minWidth: 0 }}>Submitted: {formatDate(request.createdAt)}</span>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      style={{ flexShrink: 0 }}
                      onClick={() => setExpandedKey(expanded ? null : key)}
                    >
                      {expanded ? 'Hide Details' : 'View Details'}
                    </button>
                  </div>
                </div>
              );
            }

            const request = row.request;
            const key = `renters-${request.id}`;
            const expanded = expandedKey === key;
            return (
              <div key={key} className="card">
                <div className="flex-between mb-2">
                  <h3 style={{ margin: 0, textTransform: 'capitalize', color: 'var(--color-green)' }}>
                    {request.insuranceType} Insurance
                  </h3>
                  {getStatusBadge(request.status)}
                </div>

                <div className="insurance-request-summary-grid">
                  <div>
                    <span style={{ color: '#999', fontSize: '0.9rem' }}>Street Address</span>
                    <p className="insurance-request-breakable" style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>
                      {request.streetAddress}
                    </p>
                  </div>
                  <div>
                    <span style={{ color: '#999', fontSize: '0.9rem' }}>Zip Code</span>
                    <p style={{ margin: '0.25rem 0 0', fontWeight: '600' }}>{request.zipCode}</p>
                  </div>
                </div>

                {expanded && (
                  <div
                    style={{
                      background: 'var(--color-light-grey)',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1rem',
                    }}
                  >
                    <div className="insurance-request-detail-grid">
                      <div>
                        <span style={{ color: '#6b7280' }}>Address Line 2</span>
                        <p className="insurance-request-breakable" style={{ margin: 0, fontWeight: 600 }}>
                          {request.addressLine2 || '-'}
                        </p>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280' }}>City</span>
                        <p style={{ margin: 0, fontWeight: 600 }}>{request.city}</p>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280' }}>State</span>
                        <p style={{ margin: 0, fontWeight: 600 }}>{request.state}</p>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280' }}>Country</span>
                        <p style={{ margin: 0, fontWeight: 600 }}>{request.country}</p>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280' }}>Policy start</span>
                        <p style={{ margin: 0, fontWeight: 600 }}>{formatPolicyStartDisplay(request.policyStartDate)}</p>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280' }}>Building Type</span>
                        <p style={{ margin: 0, fontWeight: 600 }}>{request.buildingType || '-'}</p>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280' }}>Safety Features</span>
                        <p style={{ margin: 0, fontWeight: 600 }}>
                          {request.safetyFeatures?.length ? request.safetyFeatures.join(', ') : '-'}
                        </p>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280' }}>Personal Liability Limit</span>
                        <p style={{ margin: 0, fontWeight: 600 }}>{formatCurrency(request.personalLiabilityLimit)}</p>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280' }}>Existing Renters Insurance</span>
                        <p style={{ margin: 0, fontWeight: 600 }}>{request.hasExistingRentersInsurance ? 'Yes' : 'No'}</p>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280' }}>Roommates</span>
                        <p style={{ margin: 0, fontWeight: 600 }}>
                          {request.roommateCount != null
                            ? request.roommateCount
                            : request.hasRoommates
                              ? 'Yes'
                              : 'No'}
                        </p>
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
                      {confirmDeleteKey === key ? (
                        <button className="btn btn-danger" onClick={() => handleDeleteRenters(request.id)}>
                          Confirm Delete?
                        </button>
                      ) : (
                        <button className="btn btn-outline" onClick={() => setConfirmDeleteKey(key)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.75rem',
                    fontSize: '0.9rem',
                    color: '#999',
                    borderTop: '1px solid var(--color-grey)',
                    paddingTop: '1rem',
                  }}
                >
                  <span style={{ minWidth: 0 }}>Submitted: {formatDate(request.createdAt)}</span>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ flexShrink: 0 }}
                    onClick={() => setExpandedKey(expanded ? null : key)}
                  >
                    {expanded ? 'Hide Details' : 'View Details'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default InsuranceRequestsPage;
